<script setup lang="ts">
import FinestField from '@/components/FinestField.vue'
import type { AmountUnit } from '@/utils/amount-unit'
import type { FieldPresentation, FieldStateMap } from '@/utils/field-state'
import { toDate, toIsoDate } from '@/utils/field-value'
import { reactive, watch } from 'vue'
import FinestFieldControl from './FinestFieldControl.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from './types'

const props = defineProps<{
  fields: InlineEditableField[]
  fieldStates?: FieldStateMap
}>()

/** Whether a field is required and what its value state is comes from the server wherever it knows the field. */
function presentationOf(field: InlineEditableField): FieldPresentation {
  const served = props.fieldStates?.[field.key]

  return {
    required: served?.required ?? Boolean(field.required),
    state: served?.state ?? field.state ?? null,
    source: served?.source ?? null,
    hint: served?.hint ?? null,
  }
}

const emit = defineEmits<{
  commit: [payload: InlineEditableCommitPayload]
}>()

const model = defineModel<InlineEditableModel>({ default: () => ({}) })

const drafts = reactive<Record<string, InlineEditableValue | Date>>({})

function draftFor(field: InlineEditableField): InlineEditableValue | Date {
  const value = model.value[field.key]

  return field.type === 'date' ? toDate(value) : value
}

function unitDraftFor(field: InlineEditableField): AmountUnit {
  return field.unitKey ? ((drafts[field.unitKey] as AmountUnit) ?? 'percent') : 'percent'
}

function referenceValueFor(field: InlineEditableField): number | null {
  const reference = field.referenceKey ? model.value[field.referenceKey] : null

  return typeof reference === 'number' ? reference : null
}

function seedDraft(field: InlineEditableField): void {
  drafts[field.key] = draftFor(field)

  if (field.unitKey) {
    drafts[field.unitKey] = model.value[field.unitKey] ?? 'percent'
  }
}

function valueChanged(field: InlineEditableField, next: InlineEditableModel, previous: InlineEditableModel): boolean {
  const keys = field.unitKey ? [field.key, field.unitKey] : [field.key]

  return keys.some((key) => next[key] !== previous[key])
}

props.fields.forEach(seedDraft)

watch(
  () => model.value,
  (next, previous) => props.fields.filter((field) => valueChanged(field, next, previous)).forEach(seedDraft),
)

watch(
  () => props.fields,
  (next, previous) => next.filter((field) => !previous.some((known) => known.key === field.key)).forEach(seedDraft),
)

function patchFor(field: InlineEditableField): InlineEditableModel {
  const draft = drafts[field.key]
  const value: InlineEditableValue =
    field.type === 'date' && draft instanceof Date ? toIsoDate(draft) : (draft as InlineEditableValue)

  if (!field.unitKey) {
    return { [field.key]: value }
  }

  return { [field.key]: value, [field.unitKey]: drafts[field.unitKey] as InlineEditableValue }
}

function commit(field: InlineEditableField): void {
  const patch = patchFor(field)
  const hasChanges = Object.entries(patch).some(([key, value]) => value !== model.value[key])

  if (!hasChanges) {
    return
  }

  model.value = { ...model.value, ...patch }
  emit('commit', { key: field.key, value: patch[field.key], field, patch })
}

function revert(field: InlineEditableField): void {
  drafts[field.key] = draftFor(field)

  if (field.unitKey) {
    drafts[field.unitKey] = model.value[field.unitKey] ?? 'percent'
  }
}

function setUnit(field: InlineEditableField, unit: AmountUnit): void {
  if (field.unitKey) {
    drafts[field.unitKey] = unit
  }
}
</script>

<template>
  <div class="grid gap-4 sm:grid-cols-2">
    <FinestField
      v-for="field in fields"
      :key="field.key"
      :label="field.label"
      :required="presentationOf(field).required"
      :state="presentationOf(field).state"
      :source="presentationOf(field).source ?? undefined"
      :hint="presentationOf(field).hint ?? ''"
      :class="{ 'sm:col-span-2': field.fullWidth }">
      <FinestFieldControl
        :field="field"
        :value="drafts[field.key]"
        :unit="unitDraftFor(field)"
        :reference-value="referenceValueFor(field)"
        :state="presentationOf(field).state"
        @update:value="drafts[field.key] = $event"
        @update:unit="setUnit(field, $event)"
        @commit="commit(field)"
        @revert="revert(field)" />
    </FinestField>
  </div>
</template>
